import { useMemo } from 'react';
import { Link } from 'react-router';
import { CustomerEquipmentTypeResponse } from '@/types';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { Check, Clock, CreditCard, CalendarDays, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvailableMonthlyRentalTiers, hasMonthlyRentalPricing } from './monthlyRentalPricing';
import { format, addDays } from 'date-fns';

export interface SelectedMonthlyRentalTier {
    tierName: string;
    months: number;
    monthlyRate: number;
    totalMinimumCost: number;
    isNoCommitment: boolean;
}

interface MonthlyRentalSelectorProps {
    product: CustomerEquipmentTypeResponse;
    /** Called when user selects a rental tier */
    onTierSelect: (tier: SelectedMonthlyRentalTier | null) => void;
    /** Currently selected tier */
    selectedTier?: SelectedMonthlyRentalTier | null;
    /** Selected pickup/start date for the rental */
    startDate?: Date | null;
    /** Called when user sets the start date */
    onStartDateChange?: (date: Date | null) => void;
    /** Whether location is required before selecting */
    requiresLocation?: boolean;
    /** Called when location is required but not set */
    onLocationRequired?: () => void;
    /** Whether the customer selected brand new item (uses premium pricing) */
    isNewItem?: boolean;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Calculate savings percentage compared to 3-month rate
 */
function calculateSavingsPercent(monthlyRate: number, baseRate: number): number {
  if (baseRate <= 0 || monthlyRate >= baseRate) return 0;
  return Math.round(((baseRate - monthlyRate) / baseRate) * 100);
}

/**
 * Monthly Rental Pricing Selector Component
 *
 * Displays monthly rental pricing options (3, 6, 12 months) with monthly rates.
 * Shows savings compared to shorter commitments and total minimum cost.
 */
export const MonthlyRentalSelector = ({
    product,
    onTierSelect,
    selectedTier,
    startDate,
    onStartDateChange,
    requiresLocation = false,
    onLocationRequired,
    isNewItem = false,
    className
}: MonthlyRentalSelectorProps) => {
    const formatPrice = useFormatPrice();
    const availableTiers = useMemo(() => getAvailableMonthlyRentalTiers(product, product, isNewItem), [product, isNewItem]);

    // Get base rate (3-month) for savings calculation
    const baseRate = useMemo(() => {
        const tier3Month = availableTiers.find(t => t.months === 3);
        return tier3Month?.monthlyRate ?? null;
    }, [availableTiers]);

    if (!hasMonthlyRentalPricing(product) || availableTiers.length === 0) {
        return null;
    }

    const handleTierClick = (tier: typeof availableTiers[0]) => {
        // Check if location is required but not set
        if (requiresLocation && onLocationRequired) {
            onLocationRequired();
            return;
        }

        if (selectedTier?.months === tier.months) {
            // Deselect if clicking the same tier
            onTierSelect(null);
        } else {
            onTierSelect(tier);
        }
    };

    return (
        <div className={cn("space-y-3", className)}>
            {/* Section Header */}
            <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent" />
                <h3 className="text-sm font-medium text-foreground">
                    Rent by the month
                </h3>
                <span className="rounded-full bg-highlight-subtle px-2 py-0.5 text-xs font-medium text-highlight">
                    Save more
                </span>
            </div>

            {/* Monthly Rental Tier Cards */}
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-4 gap-3">
                {availableTiers.map((tier) => {
                    const isSelected = selectedTier?.months === tier.months;
                    const savingsPercent = baseRate ? calculateSavingsPercent(tier.monthlyRate, baseRate) : 0;
                    const isBestValue = tier.months === 12;
                    const isFlexible = tier.isNoCommitment;

                    return (
                        <button
                            key={tier.months}
                            onClick={() => handleTierClick(tier)}
                            className={cn(
                                "relative flex min-w-0 flex-col p-4 rounded-xl border-2 transition-all text-left",
                                isSelected
                                    ? "border-primary-500 bg-primary-50 shadow-md"
                                    : "border-border hover:border-primary-300 hover:bg-secondary",
                                isBestValue && !isSelected && "border-accent/35",
                                isFlexible && !isSelected && "border-success-border"
                            )}
                            type="button"
                            aria-label={`${tier.tierName}: ${formatPrice(tier.monthlyRate)} per month${isFlexible ? ', return anytime' : ''}`}
                        >
                            {/* Best Value Badge */}
                            {isBestValue && (
                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-highlight px-3 py-0.5 text-[10px] font-semibold text-highlight-foreground">
                                    Best value
                                </span>
                            )}

                            {/* Flexible Badge */}
                            {isFlexible && (
                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-success px-3 py-0.5 text-[10px] font-semibold text-success-foreground">
                                    No commitment
                                </span>
                            )}

                            {/* Selected Check */}
                            {isSelected && (
                                <div className="absolute top-2 right-2">
                                    <Check className="h-5 w-5 text-primary" />
                                </div>
                            )}

                            {/* Tier Name */}
                            <span className={cn(
                                "mb-2 text-base font-semibold break-words",
                                isSelected ? "text-primary" : "text-foreground"
                            )}>
                                {tier.tierName}
                            </span>

                            {/* Monthly Rate */}
                            <div className="mb-1 flex flex-wrap items-baseline gap-1 min-w-0">
                                <span className={cn(
                                    "text-xl sm:text-2xl font-bold break-words",
                                    isSelected ? "text-primary" : "text-foreground"
                                )}>
                                    {formatPrice(tier.monthlyRate)}
                                </span>
                                <span className="text-xs sm:text-sm text-muted-foreground">/month</span>
                            </div>

                            {/* Savings Badge (only for commitment tiers) */}
                            {savingsPercent > 0 && !isFlexible && (
                                <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-highlight-subtle px-2 py-0.5 text-xs font-medium text-highlight">
                                    Save {savingsPercent}%
                                </span>
                            )}

                            {/* Return Anytime for flexible */}
                            {isFlexible && (
                                <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-success-subtle px-2 py-0.5 text-xs font-medium text-success">
                                    Return anytime
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Selected Tier Summary */}
            {selectedTier && (
                <div className={cn(
                    "p-4 rounded-xl border space-y-3",
                    selectedTier.isNoCommitment
                        ? "bg-success-subtle border-success-border"
                        : "bg-primary-50 border-primary-300"
                )}>
                    <div className="flex items-center justify-between">
                        <span className={cn(
                            "text-sm font-medium",
                            selectedTier.isNoCommitment ? "text-success" : "text-primary"
                        )}>
                            {selectedTier.tierName} rental
                        </span>
                        <span className={cn(
                            "text-xl font-bold",
                            selectedTier.isNoCommitment ? "text-success" : "text-primary"
                        )}>
                            {formatPrice(selectedTier.monthlyRate)}/mo
                        </span>
                    </div>

                    <div className={cn(
                        "space-y-2 text-sm min-w-0",
                        selectedTier.isNoCommitment ? "text-success" : "text-primary"
                    )}>
                        <div className="flex min-w-0 items-start gap-2">
                            <CreditCard className="h-4 w-4" />
                            <span className="break-words">Billed monthly to your card</span>
                        </div>
                        {selectedTier.isNoCommitment ? (
                            <>
                                <div className="flex min-w-0 items-start gap-2">
                                    <Check className="h-4 w-4" />
                                    <span className="break-words">No minimum commitment</span>
                                </div>
                                <div className="flex min-w-0 items-start gap-2">
                                    <CalendarDays className="h-4 w-4" />
                                    <span className="break-words">Return whenever you're done — no penalty</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex min-w-0 items-start gap-2">
                                    <Clock className="h-4 w-4" />
                                    <span className="break-words">Minimum rental period: {selectedTier.months} months</span>
                                </div>
                                <div className="flex min-w-0 items-start gap-2">
                                    <CalendarDays className="h-4 w-4" />
                                    <span className="break-words">Return early if needed — an early-return fee may apply</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Pickup Date Picker */}
                    {onStartDateChange && (
                        <div className="pt-3 border-t border-border">
                            <label htmlFor="monthly-rental-start-date" className="block text-sm font-medium text-foreground mb-1">
                                When do you need it?
                            </label>
                            <input
                                type="date"
                                id="monthly-rental-start-date"
                                value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                                onChange={(e) => {
                                    const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : null;
                                    onStartDateChange(date);
                                }}
                                min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                                className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* How it works */}
            <details className="group">
                <summary className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <span>How monthly rentals work</span>
                    <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-2 space-y-1 rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
                    <p>• <strong>Monthly billing:</strong> You're charged your rental rate once a month, automatically.</p>
                    <p>• <strong>Renting month by month:</strong> Your rental continues until you tell us you're done and return the equipment.</p>
                    <p>• <strong>Minimum rental period:</strong> With 3, 6, or 12 month rentals you agree to a minimum period in exchange for a lower monthly rate.</p>
                    <p>• <strong>Returning early:</strong> You can return the item at any time. If you return before your minimum period ends, an early-return fee may apply — based on the difference between your discounted rate and what a shorter rental would have cost:</p>
                    <ul className="ml-4 list-disc space-y-0.5">
                        <li>3-month rental returned early → difference vs. the flexible (no minimum) rate</li>
                        <li>6-month rental returned early → difference vs. the 3-month rate</li>
                        <li>12-month rental returned early → difference vs. the 6-month rate</li>
                        <li>After your minimum period → no fee</li>
                    </ul>
                    <p className="mt-1">The flexible option has no minimum period and no return fee.</p>
                    <p>• <strong>Return process:</strong> Once you're done, you have 7 days to return the equipment, then 7 days for inspection before your deposit is refunded.</p>
                    <p>• <strong>After your minimum period:</strong> Continue at the same monthly rate or return the item — no penalty either way.</p>
                    <p className="mt-2">
                        <Link to="/how-it-works" className="font-medium text-primary underline hover:text-primary/80">
                            See our full rental policy
                        </Link>
                    </p>
                </div>
            </details>
        </div>
    );
};

export default MonthlyRentalSelector;
